import usePreferences from "@hooks/usePreferences";
import React, {useEffect, useMemo, useState} from "react";
import {IssueFiltersPaginatedQuery, useGetIssuesQuery} from "@mwServices/Issue";
import {Col, Layout, Row} from "antd";
import {Helmet as Head} from "react-helmet-async";
import {Contain} from "@components/layout";
import {useGetDescriptorBySlugQuery, useGetPageBySlugQuery} from "@services/Page";
import DescriptorListFiltersDrawer from "@mwPages/descriptor-lists/DescriptorListFiltersDrawer";
import useSearchView from "@hooks/useSearchView";
import {formatNumber} from "@lib/format";
import Paragraph from "antd/lib/typography/Paragraph";
import {useNavigate} from "react-router-dom";
import IssuesResults from "@mwPages/issues/IssuesResults";
import {transformDescriptorListContent} from "@lib/transformWordPressContent";
import moment from 'moment-timezone';

const FOC_BAR_NONE = 0;
const FOC_BAR_FINALIZED = 1;
const FOC_BAR_PENDING = 2;

const DescriptorList = ({}) => {
    const { view } = useSearchView();
    const slug = view;
    const { itemCount } = usePreferences();
    const [selectedComicIds, setSelectedComicIds] = useState<number[]>([]);
    const [search, setSearch] = useState('');
    const navigate = useNavigate();
    const descriptorQuery = useGetDescriptorBySlugQuery({slug});
    const pageQuery = useGetPageBySlugQuery({slug: `descriptor-list`});

    const showFocBar = useMemo(() => {
        if(slug === 'foc'){
            const timeCheckEST = moment().tz("America/New_York");
            if(timeCheckEST.day() >= 2 && (timeCheckEST.day() < 5 || (timeCheckEST.day() === 5 && timeCheckEST.hours() < 18))){
                return FOC_BAR_PENDING;
            }else{
                return FOC_BAR_FINALIZED;
            }
        }

        return FOC_BAR_NONE;
    }, [slug]);

    const { title, description, image_src, descriptor_id } = useMemo(() => {
        if(descriptorQuery.data) {
            if(descriptorQuery.data.success === 0){
                navigate("/404?view="+view)
            }else {
                return descriptorQuery.data;
            }
        }
        return {title: '', description: '', image_src: '', descriptor_id: -1};
    }, [descriptorQuery]);

    const activeShopLink = useMemo(() => {
        if(pageQuery.data) {
            const pageContent = transformDescriptorListContent(pageQuery.data);
            const foundLink = pageContent && pageContent.shopLinks.find(link => link.active && link.slug === slug);
            
            if (foundLink && slug === 'foc') {
                const className = foundLink.subtitle &&
                    typeof foundLink.subtitle === "string" &&
                    foundLink.subtitle.length < 255 &&
                    !foundLink.subtitle.includes('"') &&
                    !foundLink.subtitle.includes("'") &&
                    !/^https?:\/\//i.test(foundLink.subtitle) &&
                    !/<[^>]*>/g.test(foundLink.subtitle) // no HTML tags
                        ? foundLink.subtitle
                        : "";
                
                return {
                    ...foundLink,
                    className
                };
            }
            
            return foundLink;
        }
        return null;
    }, [pageQuery, slug]);

    const defaultFilters = useMemo(() => ({
        descriptorId: descriptor_id,
        query: '',
        sort: 0,
        showVariants: true, //This means include variants in the results, not only variants.
        inCollection: false,
        isHot: false,
        isKey: false,
        isRare: false,
        multipleDateRanges: undefined,
        inWishlist: undefined,
    }), [descriptor_id]);

    const [issueParams, setIssueParams] = useState<IssueFiltersPaginatedQuery>({
        filters: { ...defaultFilters },
        page: 1,
        perPage: itemCount,
    });

    useEffect(() => {
        setIssueParams({
            filters: { ...defaultFilters },
            page: 1,
            perPage: itemCount,
        });
    }, [defaultFilters, itemCount]);

    useEffect(() => {
        if(search !== issueParams.filters.query){
            setIssueParams({ ...issueParams, page: 1, filters: { ...issueParams.filters, query: search ? search : '' } });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    useEffect(() => {
        if(issueParams.filters.query && search !== issueParams.filters.query){
            setSearch(issueParams.filters.query);
        }
    }, [issueParams.filters.query]);

    const issueQuery = useGetIssuesQuery(issueParams);

    const issueData = useMemo(() => {
        if(issueQuery.data) {
            return issueQuery.data
        }
        return { pagination: null, issues: null, overview: null};
    }, [issueQuery]);
    const pagination = useMemo(() => issueData.pagination, [issueData]);

    return (
        <Layout className="page descriptor-list">
            <Head>
                <title>{title} - CovrPrice</title>
            </Head>

            <div
                className="list-header"
                style={{backgroundImage: `linear-gradient( to top right, rgba(14, 32, 45, 1), rgba(14, 32, 45, 0.5) ),url(${image_src})`}}
            >
                <Row>
                    <Col span={24} className="header-content">
                        <Paragraph className={"heading-text"} ellipsis={{ rows: 1 }}>{title}</Paragraph>
                        <Paragraph className={"sub-heading-text"} ellipsis={{ rows: 3 }}>{description}</Paragraph>
                    </Col>
                </Row>
            </div>
            {showFocBar === FOC_BAR_PENDING && (
                <div style={{backgroundColor: "#ffdc39", padding: 10, textAlign: "center", fontWeight: "bold"}}>FOC STATUS: Pending (Finalized on Friday @ 6PM EDT)</div>
            )}
            {showFocBar === FOC_BAR_FINALIZED && (
                <div style={{backgroundColor: "#7bdf4a", padding: 10, textAlign: "center", fontWeight: "bold"}}>FOC STATUS: Finalized</div>
            )}
            <Contain>

                <IssuesResults
                    issueQuery={issueQuery}
                    issuesTitle={<>{formatNumber(pagination ? formatNumber(pagination.total_items) : 0)} Issues</>}
                    infoModalData={null}
                    defaultFilters={defaultFilters}
                    issueParams={issueParams}
                    setIssueParams={setIssueParams}
                    search={search}
                    setSearch={setSearch}
                    selectedComicIds={selectedComicIds}
                    setSelectedComicIds={setSelectedComicIds}
                    filterDrawer={
                        <DescriptorListFiltersDrawer
                            filters={issueParams.filters}
                            setFilters={(filters) => setIssueParams({...issueParams, page: 1, filters})}
                            defaultFilters={defaultFilters}
                        />
                    }
                    isSearchBarcode={false}
                    isSearchES={false}
                    useGridView={false}
                    useListView={true}
                    useListViewNoImages={false}
                    showIssueNotes={true}
                    shopLink={activeShopLink}
                />

            </Contain>
        </Layout>
    );
};

export default DescriptorList;
